import TableContainer from 'components/TableContainer';
import Vocabulary from 'components/Vocabulary';
import React, { useCallback, useMemo } from 'react'
import _ from 'lodash';
import ModalUtil from 'utils/modalUtil';
import { getFilterProps, Sorters } from 'utils/tableUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import MSTeamsMappingForm from './MSTeamsMappingForm';
import AuthorisedSwitch from 'components/AuthorisedSwitch';
import WarningPopup from 'components/WarningPopup';
import ActionMenuV2 from 'components/ActionMenuV2';
import { Collapse, Tooltip, Spin, Row, Col, Typography, Button, message, Alert } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import AppLoader from 'components/AppLoader';
import AuthorisedContainer from 'components/AuthorisedContainer';
import DeleteButton from 'components/DeleteButton';
import { LoadingIcon } from 'components/Icons';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { CellRenderers } from 'utils/tableUtils';
import { ShowMorePopover } from 'views/Insights/insightsUtils';
import MSTeamsOAuthPopup from './MSTeamsOAuthPopup';

const items = [{
  type: "edit",
  permission: PermissionSetMap.EDIT_TOOL_CONFIG
}, {
  type: "delete",
  permission: PermissionSetMap.DELETE_TOOL
}]

export default function MSTeamsListing({ data, onUpdateConfigComponent, loginConfigList, loading, ...props }) {
  const msTeamsArr = []
  if (!_.isEmpty(loginConfigList)) {
    for (const msTeam of loginConfigList) {
      msTeamsArr.push(msTeam)
    }
  }
  return (
    <Spin spinning={loading} indicator={<AppLoader />}>
      {msTeamsArr.map((msTeam, i) =>
        <ConfigItem
          loading={loading}
          teamData={msTeam}
          loginId={msTeam.id}
          data={data}
          onUpdateConfigComponent={onUpdateConfigComponent}
          key={i}
          index={i} {...props} />)}
    </Spin>
  )
}

function ConfigItem({ data, onUpdateConfigComponent, loginId, teamData, loading, loginConfigurations, history, integrationData }) {
  const selfData = useMemo(() => _.filter(data, config => config.configId === loginId), [data, loginId])
  const numberofConfig = selfData.length;

  const onAddMapping = useCallback(async () => {
    ModalUtil.show({
      content: <MSTeamsMappingForm loginId={loginId} config={data} onUpdateConfigComponent={onUpdateConfigComponent} />,
    })
  }, [data, loginId, onUpdateConfigComponent]);

  const onDelete = useCallback(async () => {
    try {
      await ToolsIntegrationsAPI.deleteMSTeamsLogin(loginId).promise;
      message.success("MS Teams Deleted Successfully")
      onUpdateConfigComponent()
    }
    catch (e) {
      console.log(e)
    }
  }, [loginId, onUpdateConfigComponent]);

  const onReauthenticate = useCallback(() => {
    ModalUtil.show({
      content: <MSTeamsOAuthPopup configList={data} onUpdateConfigComponent={onUpdateConfigComponent} {...integrationData} data={data} history={history} loginConfigList={loginConfigurations} />,
      afterClose: (data) => data && onUpdateConfigComponent(true)
  })
  }, [data, history, integrationData, loginConfigurations, onUpdateConfigComponent])

  return (
    <Collapse
      className="pull-based-tool"
      bordered={false}
      expandIconPosition="right"
      expandIcon={({ isActive }) => <Tooltip title={isActive ? "Close Mapping" : "Open Mapping"}><CaretDownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 20 }} /></Tooltip>}
    >
      <Collapse.Panel key={loginId} header={panelHeader(teamData, numberofConfig)}>
        <div className="d-flex align-items-center justify-content-flex-end m-b-sm">

          {!teamData.refreshTokenFunctional &&  <>
            <Alert type="warning" className='p-0' description={"Your refresh token seems to be expired, please login again"} />
            <div className='m-l-sm'>
            <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Button size="small" type="primary" onClick={onReauthenticate}>Reauthenticate</Button>
          </AuthorisedContainer></div></>}
          <div className='m-l-sm'>
          <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Button size="small" type="primary" onClick={onAddMapping}>Add Mapping</Button>
          </AuthorisedContainer>
          </div>
          <div className='m-l-sm'>
            <AuthorisedContainer tooltip={"Delete Team"} permission={PermissionSetMap.DELETE_TOOL}>
              <DeleteButton onDelete={onDelete} type="icon" />
            </AuthorisedContainer>
          </div>
        </div>
        <Spin spinning={_.isUndefined(data)} indicator={<LoadingIcon />}>
          <ConfigTable loading={loading} data={selfData} loginId={loginId} onUpdateConfigComponent={onUpdateConfigComponent} />
        </Spin>
      </Collapse.Panel>
    </Collapse>
  )
}

const ToggleButton = ({ text, record, onUpdateConfigComponent }) => {
  const confirmPopUp = useCallback(() => {
    ModalUtil.show({
      content: <WarningPopup
        type=""
        title={`Are you sure you want to ${record.enabled ? "disable?" : "enable?"}`}
        onOk={() => {
          const { promise, cancel } = ToolsIntegrationsAPI.updateMSTeamIntegrationDetails({ ...record, enabled: !record.enabled }, record?.id);
          promise.then(() => onUpdateConfigComponent())
          return cancel;
        }} note />
    })
  }, [record, onUpdateConfigComponent])

  return (<AuthorisedSwitch permission={PermissionSetMap.EDIT_TOOL_CONFIG} defaultChecked={text} checked={record.enabled} onClick={confirmPopUp} size="small" />)
}

function ConfigTable({ data, onUpdateConfigComponent, loginId }) {
  const onDelete = useCallback(async (record) => {
    try {
      await ToolsIntegrationsAPI.deleteMSTeamsConfiguration(record.id).promise;
      onUpdateConfigComponent();
    } catch (err) {
      console.log(err)
    }
  }, [onUpdateConfigComponent])

  const onClick = useCallback(async (_, record) => {
    switch (record.name) {
      case "edit":
        ModalUtil.show({
          content: <MSTeamsMappingForm config={record.data} loginId={loginId} isEdit={true} onUpdateConfigComponent={onUpdateConfigComponent} />,
        });
        break;
      case "delete":
        await onDelete(record.data)
        break;
      default:
        break;
    }
  }, [loginId, onUpdateConfigComponent, onDelete])

  const columns = useMemo(() => [{
    title: <Vocabulary name="product" />,
    dataIndex: "apName",
    sorter: Sorters.STRING("apName"),
    ...getFilterProps({ dataIndex: "apName", dataSource: data }),
    render: (text) => text || 'All',
    width: 150
  }, {
    title: <Vocabulary name="subProduct" />,
    dataIndex: "aspName",
    sorter: Sorters.STRING("aspName"),
    ...getFilterProps({ dataIndex: "aspName", dataSource: data }),
    render: (aspName) => aspName || "All",
    width: 150
  }, {
    title: "Environment",
    dataIndex: "aeName",
    ...getFilterProps({ dataIndex: "aeName", filterLabelFormatter: (aeName) => CellRenderers.VOCABULARY_RENDERER(aeName), dataSource: data }),
    ellipsis: true,
    width: 120,
    render: (aeName) => aeName ? <EnvironmentName name={aeName} /> : "All",
  },
  {
    title: "Team",
    dataIndex: "teamName",
    ...getFilterProps({ dataIndex: "teamName", dataSource: data }),
    width: 120,
    ellipsis: true
  },
  {
    title: "Channel",
    dataIndex: "channelIdsAndNames",
    ...getFilterProps({ filters: [...new Set(_.flatten(_.map(data, channel => _.values(channel.channelIdsAndNames))))], noFilterFunction: true }),
    onFilter: (value, record) => {
      return !!_.values(record.channelIdsAndNames).find((channelName) => channelName === value);
    },
    render: (channelIdsAndNames) =>  ShowMorePopover(_.values(channelIdsAndNames), channelIdsAndNames, 2, _.noop, 'tag', undefined, {className: 'text-ellipsis', wrapperStyle: { display: 'flex' }}),
    width: 120,
    ellipsis: true,
  },
  {
    title: 'Config',
    dataIndex: 'enabled',
    width: 100,
    render: (text, record) => {
      return <ToggleButton text={text} record={record} onUpdateConfigComponent={onUpdateConfigComponent}/>
    }
  }, {
    title: "Action",
    dataIndex: "id",
    key: 'action',
    width: 50,
    render: (id, obj) => {
      return (
        <ActionMenuV2 items={items} onClick={onClick} data={obj} />
      )
    },
  }], [data, onClick, onUpdateConfigComponent])

  return (
    <TableContainer
      columns={columns}
      dataSource={data}
      loading={_.isUndefined(data)}
      locale={{ emptyText: "No configurations" }}
    />
  )
}

function panelHeader(teamData, numberofConfig) {
  const { name } = teamData;
  return (
    <Row className="align-items-center">
      <Col span={20}>
        {name && <Typography.Text className="m-r-l">Team: {name}</Typography.Text>}
        {<Typography.Text className="m-r-l">{numberofConfig === 0 ? "No Mappings Available" : `Mappings: ${numberofConfig}`}</Typography.Text>}
      </Col>
    </Row>
  )
}
